import React, { useMemo } from "react";
import "./StudentSchoolTab.css";

const ALL_COURSE_COLUMNS = [
  { key: "g1", label: "1st" },
  { key: "g2", label: "2nd" },
  { key: "g3", label: "3rd" },
  { key: "g4", label: "4th" },
  { key: "g5", label: "5th" },
  { key: "g6", label: "6th" },
  { key: "g7", label: "7th" },
  { key: "g8", label: "8th" },
  { key: "g9", label: "9th" },
  { key: "g10", label: "10th" },
  { key: "g11", label: "11th" },
  { key: "g12", label: "12th" },
];

const DEFAULT_COURSE_ROW_COUNT = 6;

const BAND_COLUMNS = {
  elementary: ["g1", "g2", "g3", "g4", "g5"],
  middle: ["g6", "g7", "g8"],
  high: ["g9", "g10", "g11", "g12"],
};

function getGradeBand(gradeRaw) {
  const raw = String(gradeRaw ?? "").trim().toLowerCase();
  if (!raw) return "high";
  if (raw === "k" || raw === "kg" || raw === "kindergarten") return "elementary";

  const numeric = Number(raw);
  if (!Number.isFinite(numeric)) return "high";
  if (numeric <= 5) return "elementary";
  if (numeric <= 8) return "middle";
  return "high";
}

function getBandTitle(band) {
  if (band === "elementary") return "Elementary School Courses";
  if (band === "middle") return "Middle School Courses";
  return "High School Courses";
}

function createCourseRow() {
  const row = { id: `${Date.now()}-${Math.random()}` };
  for (const col of ALL_COURSE_COLUMNS) row[col.key] = "";
  return row;
}

function toIsoDateString(dateObj) {
  return new Date(dateObj.getTime() - dateObj.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
}

function levelToColumn(level) {
  const raw = String(level || "").toLowerCase();
  const found = ALL_COURSE_COLUMNS.find((c) => raw.startsWith(c.label.toLowerCase().replace("th", "")));
  if (found) return found.key;
  if (raw.startsWith("1")) return "g1";
  if (raw.startsWith("2")) return "g2";
  if (raw.startsWith("3")) return "g3";
  if (raw.startsWith("4")) return "g4";
  if (raw.startsWith("5")) return "g5";
  if (raw.startsWith("6")) return "g6";
  if (raw.startsWith("7")) return "g7";
  if (raw.startsWith("8")) return "g8";
  if (raw.startsWith("9")) return "g9";
  if (raw.startsWith("10")) return "g10";
  if (raw.startsWith("11")) return "g11";
  if (raw.startsWith("12")) return "g12";
  return "g9";
}

function normalizeCourseRows(student) {
  const tableRows = Array.isArray(student?.schoolCoursesTable) ? student.schoolCoursesTable : [];
  if (tableRows.length > 0) {
    return tableRows.map((row) => {
      const normalized = { id: row?.id || `${Date.now()}-${Math.random()}` };
      for (const col of ALL_COURSE_COLUMNS) {
        normalized[col.key] = String(row?.[col.key] || "");
      }
      return normalized;
    });
  }

  const legacyCourses = Array.isArray(student?.highSchoolCourses) ? student.highSchoolCourses : [];
  if (legacyCourses.length > 0) {
    const migrated = legacyCourses.map((course) => {
      const next = createCourseRow();
      next[levelToColumn(course?.level)] = String(course?.name || "");
      return next;
    });
    while (migrated.length < DEFAULT_COURSE_ROW_COUNT) migrated.push(createCourseRow());
    return migrated;
  }

  return Array.from({ length: DEFAULT_COURSE_ROW_COUNT }, () => createCourseRow());
}

function extractSubjects(courseRows, visibleColumns) {
  const ordered = [];
  const seen = new Set();
  for (const row of courseRows) {
    for (const col of visibleColumns) {
      const subject = String(row?.[col.key] || "").trim();
      if (!subject) continue;
      const key = subject.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      ordered.push(subject);
    }
  }
  return ordered;
}

function flattenLegacyHighSchoolCourses(courseRows) {
  const list = [];
  for (const row of courseRows) {
    for (const col of ALL_COURSE_COLUMNS) {
      const name = String(row?.[col.key] || "").trim();
      if (!name) continue;
      list.push({
        id: `${row.id}-${col.key}`,
        name,
        level: col.label,
      });
    }
  }
  return list;
}

function normalizeGradeSnapshots(student) {
  const snapshots = Array.isArray(student?.schoolGradeSnapshots) ? student.schoolGradeSnapshots : [];
  if (snapshots.length > 0) {
    return snapshots.map((snap) => ({
      id: snap?.id || `${Date.now()}-${Math.random()}`,
      date: String(snap?.date || ""),
      scores: snap?.scores && typeof snap.scores === "object" ? { ...snap.scores } : {},
    }));
  }

  const legacyCards = Array.isArray(student?.schoolGrades) ? student.schoolGrades : [];
  if (legacyCards.length > 0) {
    const byDate = new Map();
    for (const card of legacyCards) {
      const subject = String(card?.subject || "").trim();
      const grades = Array.isArray(card?.grades) ? card.grades : [];
      for (const grade of grades) {
        const date = String(grade?.date || "");
        const dateKey = date || "__undated__";
        if (!byDate.has(dateKey)) {
          byDate.set(dateKey, {
            id: `${dateKey}-${Math.random()}`,
            date,
            scores: {},
          });
        }
        if (subject) {
          byDate.get(dateKey).scores[subject] = String(grade?.val ?? "");
        }
      }
    }
    if (byDate.size > 0) return Array.from(byDate.values());
  }

  return [
    {
      id: `${Date.now()}-${Math.random()}`,
      date: toIsoDateString(new Date()),
      scores: {},
    },
  ];
}

function toLegacySchoolGrades(subjects, snapshots) {
  return subjects.map((subject) => ({
    id: `${subject}-${Math.random()}`,
    subject,
    grades: snapshots.map((snap) => ({
      date: snap.date || "",
      val: String((snap.scores || {})[subject] || ""),
    })),
    examDate: "",
    notes: "",
  }));
}

export default function StudentSchoolTab({ student, setField }) {
  const gradeBand = useMemo(() => getGradeBand(student?.grade), [student?.grade]);
  const visibleColumns = useMemo(
    () => ALL_COURSE_COLUMNS.filter((col) => BAND_COLUMNS[gradeBand].includes(col.key)),
    [gradeBand]
  );
  const schoolCoursesTitle = useMemo(() => getBandTitle(gradeBand), [gradeBand]);

  const courseRows = useMemo(() => normalizeCourseRows(student), [student]);
  const subjects = useMemo(() => extractSubjects(courseRows, visibleColumns), [courseRows, visibleColumns]);
  const gradeSnapshots = useMemo(() => normalizeGradeSnapshots(student), [student]);

  const persistCourseRows = (nextRows) => {
    const nextSubjects = extractSubjects(nextRows, visibleColumns);
    setField("schoolCoursesTable", nextRows);
    setField("highSchoolCourses", flattenLegacyHighSchoolCourses(nextRows));
    setField("schoolGrades", toLegacySchoolGrades(nextSubjects, gradeSnapshots));
  };

  const persistSnapshots = (nextSnapshots) => {
    setField("schoolGradeSnapshots", nextSnapshots);
    setField("schoolGrades", toLegacySchoolGrades(subjects, nextSnapshots));
  };

  const updateCourseCell = (rowIndex, colKey, value) => {
    const next = [...courseRows];
    next[rowIndex] = { ...next[rowIndex], [colKey]: value };
    persistCourseRows(next);
  };

  const addCourseRow = () => {
    persistCourseRows([...courseRows, createCourseRow()]);
  };

  const updateSnapshotDate = (snapshotIndex, dateValue) => {
    const next = [...gradeSnapshots];
    next[snapshotIndex] = { ...next[snapshotIndex], date: dateValue };
    persistSnapshots(next);
  };

  const updateScoreCell = (snapshotIndex, subject, scoreValue) => {
    const next = [...gradeSnapshots];
    const current = next[snapshotIndex];
    next[snapshotIndex] = {
      ...current,
      scores: { ...(current.scores || {}), [subject]: scoreValue },
    };
    persistSnapshots(next);
  };

  const addSnapshotColumn = () => {
    persistSnapshots([
      ...gradeSnapshots,
      {
        id: `${Date.now()}-${Math.random()}`,
        date: toIsoDateString(new Date()),
        scores: {},
      },
    ]);
  };

  return (
    <div className="student-school-tab sdw-stack">
      <section className="sdw-section">
        <div className="sdw-section__head">
          <div className="sdw-section__title">Courses</div>
          <div className="sdw-section__sub">{schoolCoursesTitle}</div>
        </div>
        <div className="sdw-section__body">
          <div className="student-school-tab__table-wrap">
            <table className="student-school-tab__grid-table">
              <thead>
                <tr>
                  <th className="student-school-tab__index-col">#</th>
                  {visibleColumns.map((col) => (
                    <th key={col.key}>{col.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {courseRows.map((row, rowIdx) => (
                  <tr key={row.id || rowIdx}>
                    <td className="student-school-tab__index-col">{rowIdx + 1}</td>
                    {visibleColumns.map((col) => (
                      <td key={col.key}>
                        <input
                          value={row[col.key] || ""}
                          onChange={(e) => updateCourseCell(rowIdx, col.key, e.target.value)}
                          placeholder={rowIdx === 0 ? "Course" : ""}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button type="button" className="sdw-btn sdw-btn--sm student-school-tab__add-btn" onClick={addCourseRow}>
            + Add Row
          </button>
        </div>
      </section>

      <section className="sdw-section">
        <div className="sdw-section__head">
          <div className="sdw-section__title">Grades</div>
          <button type="button" className="sdw-btn sdw-btn--sm" onClick={addSnapshotColumn}>
            + New Date Column
          </button>
        </div>
        <div className="sdw-section__body">
          {subjects.length === 0 ? (
            <div className="sdw-empty">Add courses above to start tracking grades.</div>
          ) : (
            <div className="student-school-tab__table-wrap">
              <table className="student-school-tab__grade-table">
                <thead>
                  <tr>
                    <th className="student-school-tab__index-col">#</th>
                    <th className="student-school-tab__subject-col">Subject</th>
                    {gradeSnapshots.map((snap, snapIdx) => (
                      <th key={snap.id || snapIdx}>
                        <input
                          type="date"
                          value={snap.date || ""}
                          onChange={(e) => updateSnapshotDate(snapIdx, e.target.value)}
                        />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {subjects.map((subject, subjectIdx) => (
                    <tr key={subject}>
                      <td className="student-school-tab__index-col">{subjectIdx + 1}</td>
                      <td className="student-school-tab__subject-col">{subject}</td>
                      {gradeSnapshots.map((snap, snapIdx) => (
                        <td key={snap.id || snapIdx}>
                          <input
                            value={String((snap.scores || {})[subject] || "")}
                            onChange={(e) => updateScoreCell(snapIdx, subject, e.target.value)}
                            placeholder="Grade"
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      <div className="student-school-tab__split">
        <section className="sdw-section">
          <div className="sdw-section__head">
            <div className="sdw-section__title">Plan</div>
          </div>
          <div className="sdw-section__body">
            <textarea
              className="student-school-tab__textarea"
              placeholder="Write plan here…"
              value={student?.schoolPlan || ""}
              onChange={(e) => setField("schoolPlan", e.target.value)}
            />
          </div>
        </section>

        <section className="sdw-section">
          <div className="sdw-section__head">
            <div className="sdw-section__title">Login Information</div>
          </div>
          <div className="sdw-section__body">
            <textarea
              className="student-school-tab__textarea"
              placeholder="Write login information here…"
              value={student?.schoolLogin || ""}
              onChange={(e) => setField("schoolLogin", e.target.value)}
            />
          </div>
        </section>
      </div>
    </div>
  );
}
